import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { forexApi } from "@/lib/forex-api";

interface ProfitRecord {
  id: string;
  name: string;
  type: "follow_relation" | "agent" | "merchant" | "master" | "symbol";
  parentLabel: string;
  netProfit: number;
  returnPct: number;
  maxDrawdown: number;
  winRate: number;
  copyCount: number;
  status: "active" | "paused" | "stopped" | string;
  link: string;
}

const typeLabels: Record<string, string> = {
  follow_relation: "跟单关系",
  agent: "智能体",
  merchant: "商家",
  master: "主账号",
  symbol: "交易品种",
};
const typeChipColors: Record<string, string> = {
  follow_relation: "bg-brand/10 text-brand",
  agent: "bg-brand/10 text-brand",
  merchant: "bg-amber-500/10 text-amber-500",
  master: "bg-emerald-500/10 text-emerald-500",
  symbol: "bg-orange-500/10 text-orange-500",
};

function asNumber(value: unknown, fallback = 0) {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function asString(value: unknown, fallback = "--") {
  return typeof value === "string" && value ? value : fallback;
}

function asStringArray(value: unknown) {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : [];
}

export default function ProfitDashboardPage() {
  const [days, setDays] = useState(90);
  const [dimension, setDimension] = useState<string>("all");
  const [sortBy, setSortBy] = useState("netProfit");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [records, setRecords] = useState<ProfitRecord[]>([]);
  const [summary, setSummary] = useState<Record<string, number | string>>({});
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    async function load() {
      setLoading(true);
      try {
        const [dashboard, agents] = await Promise.all([forexApi.getProfitDashboard(), forexApi.listConfigAgents()]);
        if (!active) return;
        setSummary({ ...dashboard.summary, active_agents: agents.items.filter((item) => item.runtime === "running").length });
        setRecords(dashboard.items.map((item) => {
          const type = asString(item.object_type, "follow_relation") as ProfitRecord["type"];
          const id = asString(item.object_id);
          const symbols = asStringArray(item.symbols);
          return {
            id,
            type,
            name: asString(item.name),
            parentLabel: symbols.length ? symbols.join(" / ") : "外汇跟单统计对象",
            netProfit: asNumber(item.net_profit),
            returnPct: asNumber(item.success_rate) - 50,
            maxDrawdown: asNumber(item.max_drawdown, asNumber(dashboard.summary.max_drawdown)),
            winRate: asNumber(item.success_rate),
            copyCount: asNumber(item.copy_count, 1),
            status: asString(item.status, "active"),
            link: `/forex/profit-dashboard/agent-detail?id=${encodeURIComponent(id)}`,
          };
        }));
      } catch (error) {
        if (active) setToast("盈利大盘加载失败，请检查后台服务");
      } finally {
        if (active) setLoading(false);
      }
    }
    load();
    return () => {
      active = false;
    };
  }, []);

  const filtered = useMemo(
    () => records.filter((record) => dimension === "all" || record.type === dimension),
    [records, dimension],
  );

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const va = a[sortBy as keyof ProfitRecord];
      const vb = b[sortBy as keyof ProfitRecord];
      if (typeof va !== "number" || typeof vb !== "number") return 0;
      return sortDir === "desc" ? vb - va : va - vb;
    });
  }, [filtered, sortBy, sortDir]);

  const handleSort = (col: string) => {
    if (sortBy === col) setSortDir((dir) => (dir === "desc" ? "asc" : "desc"));
    else {
      setSortBy(col);
      setSortDir("desc");
    }
  };

  const SortIcon = ({ col }: { col: string }) => (
    <i className={`text-[10px] ml-1 ${sortBy === col ? (sortDir === "desc" ? "ri-sort-desc" : "ri-sort-asc") : "ri-expand-up-down-line text-text-muted"}`} />
  );

  const formatPnl = (value: number) => {
    const cls = value >= 0 ? "text-emerald-500" : "text-red-500";
    const sign = value >= 0 ? "+" : "-";
    return <span className={`font-mono text-xs whitespace-nowrap ${cls}`}>{sign}${Math.abs(value).toLocaleString("en-US", { minimumFractionDigits: 2 })}</span>;
  };

  const viewSummary = {
    totalProfit: asNumber(summary.net_profit, sorted.reduce((sum, record) => sum + record.netProfit, 0)),
    activeRelations: asNumber(summary.active_follow_relations, sorted.filter((record) => record.status === "active").length),
    activeAgents: asNumber(summary.active_agents),
    maxDrawdown: asNumber(summary.max_drawdown, sorted.length ? Math.max(...sorted.map((record) => record.maxDrawdown)) : 0),
    avgWinRate: sorted.length ? (sorted.reduce((sum, record) => sum + record.winRate, 0) / sorted.length).toFixed(1) : "0.0",
    objectCount: sorted.length,
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-semibold text-text-primary">盈利表现大盘</h2>
          <p className="text-xs text-text-muted mt-0.5">按智能体、主账号、跟单关系等维度统计收益与风险表现</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center rounded-lg bg-surface border border-surface-border p-0.5">
            {[7, 30, 90, 180].map((day) => (
              <button key={day} onClick={() => setDays(day)} className={`px-3 py-1.5 rounded-md text-[11px] font-medium transition-colors cursor-pointer whitespace-nowrap ${days === day ? "bg-white text-text-primary shadow-sm" : "text-text-muted hover:text-text-secondary"}`}>
                {day}天
              </button>
            ))}
          </div>
          <select value={dimension} onChange={(event) => setDimension(event.target.value)} className="h-8 px-2.5 rounded-lg bg-surface text-xs text-text-primary border border-surface-border focus:border-brand/50 focus:outline-none cursor-pointer">
            <option value="all">全部维度</option>
            <option value="follow_relation">跟单关系</option>
            <option value="agent">智能体</option>
            <option value="merchant">商家</option>
            <option value="master">主账号</option>
            <option value="symbol">交易品种</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <div className="bg-white rounded-lg border border-surface-border px-4 py-3"><p className="text-[11px] text-text-muted">净收益（{days}天）</p><p className={`text-sm font-semibold font-mono mt-0.5 ${viewSummary.totalProfit >= 0 ? "text-emerald-500" : "text-red-500"}`}>{viewSummary.totalProfit >= 0 ? "+" : "-"}${Math.abs(viewSummary.totalProfit).toLocaleString("en-US", { minimumFractionDigits: 2 })}</p></div>
        <div className="bg-white rounded-lg border border-surface-border px-4 py-3"><p className="text-[11px] text-text-muted">活跃跟单</p><p className="text-sm font-semibold font-mono mt-0.5 text-text-primary">{viewSummary.activeRelations}</p></div>
        <div className="bg-white rounded-lg border border-surface-border px-4 py-3"><p className="text-[11px] text-text-muted">活跃智能体</p><p className="text-sm font-semibold font-mono mt-0.5 text-text-primary">{viewSummary.activeAgents}</p></div>
        <div className="bg-white rounded-lg border border-surface-border px-4 py-3"><p className="text-[11px] text-text-muted">最大回撤</p><p className={`text-sm font-semibold font-mono mt-0.5 ${viewSummary.maxDrawdown >= 30 ? "text-red-500" : viewSummary.maxDrawdown >= 15 ? "text-amber-500" : "text-emerald-500"}`}>{viewSummary.maxDrawdown}%</p></div>
        <div className="bg-white rounded-lg border border-surface-border px-4 py-3"><p className="text-[11px] text-text-muted">平均胜率</p><p className="text-sm font-semibold font-mono mt-0.5 text-text-primary">{viewSummary.avgWinRate}%</p></div>
        <div className="bg-white rounded-lg border border-surface-border px-4 py-3"><p className="text-[11px] text-text-muted">统计对象</p><p className="text-sm font-semibold font-mono mt-0.5 text-text-primary">{viewSummary.objectCount}</p></div>
      </div>

      <div className="bg-white rounded-lg border border-surface-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs min-w-[1100px]">
            <thead>
              <tr className="border-b border-surface-border bg-surface/50">
                <th className="text-left px-4 py-2.5 text-text-secondary font-medium text-[11px]">名称</th>
                <th className="text-left px-4 py-2.5 text-text-secondary font-medium text-[11px]">类型</th>
                <th className="text-right px-4 py-2.5 text-text-secondary font-medium text-[11px] cursor-pointer" onClick={() => handleSort("netProfit")}>净收益<SortIcon col="netProfit" /></th>
                <th className="text-right px-4 py-2.5 text-text-secondary font-medium text-[11px] cursor-pointer" onClick={() => handleSort("returnPct")}>收益率<SortIcon col="returnPct" /></th>
                <th className="text-right px-4 py-2.5 text-text-secondary font-medium text-[11px] cursor-pointer" onClick={() => handleSort("maxDrawdown")}>最大回撤<SortIcon col="maxDrawdown" /></th>
                <th className="text-right px-4 py-2.5 text-text-secondary font-medium text-[11px] cursor-pointer" onClick={() => handleSort("winRate")}>胜率<SortIcon col="winRate" /></th>
                <th className="text-center px-4 py-2.5 text-text-secondary font-medium text-[11px]">状态</th>
                <th className="text-center px-4 py-2.5 text-text-secondary font-medium text-[11px]">操作</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-text-muted">加载盈利大盘...</td></tr>
              ) : sorted.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-text-muted">暂无统计对象</td></tr>
              ) : sorted.map((record) => (
                <tr key={record.id} className="border-b border-surface-border hover:bg-surface/30 transition-colors">
                  <td className="px-4 py-2.5"><span className="text-text-primary text-xs font-medium whitespace-nowrap">{record.name}</span><p className="text-text-muted text-[10px]">{record.parentLabel}</p></td>
                  <td className="px-4 py-2.5"><span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium ${typeChipColors[record.type] || "bg-surface text-text-secondary"}`}>{typeLabels[record.type] || record.type}</span></td>
                  <td className="px-4 py-2.5 text-right">{formatPnl(record.netProfit)}</td>
                  <td className="px-4 py-2.5 text-right"><span className={`font-mono text-xs ${record.returnPct >= 0 ? "text-emerald-500" : "text-red-500"}`}>{record.returnPct >= 0 ? "+" : ""}{record.returnPct.toFixed(1)}%</span></td>
                  <td className="px-4 py-2.5 text-right"><span className={`font-mono text-xs ${record.maxDrawdown >= 30 ? "text-red-500" : record.maxDrawdown >= 15 ? "text-amber-500" : "text-emerald-500"}`}>{record.maxDrawdown}%</span></td>
                  <td className="px-4 py-2.5 text-right"><span className={`font-mono text-xs ${record.winRate >= 60 ? "text-emerald-500" : record.winRate >= 50 ? "text-amber-500" : "text-red-500"}`}>{record.winRate}%</span></td>
                  <td className="px-4 py-2.5 text-center"><span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${record.status === "active" ? "bg-emerald-500/10 text-emerald-500" : "bg-amber-500/10 text-amber-500"}`}><i className={`text-[8px] ${record.status === "active" ? "ri-play-circle-fill" : "ri-pause-circle-fill"}`} />{record.status === "active" ? "运行中" : "非运行"}</span></td>
                  <td className="px-4 py-2.5 text-center"><Link to={record.link} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-medium bg-surface text-text-secondary hover:bg-brand/10 hover:text-brand transition-colors cursor-pointer whitespace-nowrap"><i className="ri-eye-line text-[11px]" />详情</Link></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {toast && (
        <div className="fixed bottom-6 right-6 z-50">
          <div className="flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg text-sm font-medium bg-red-500 text-white"><i className="ri-close-circle-fill text-base" />{toast}</div>
        </div>
      )}
    </div>
  );
}
